import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { Box, Chip, Typography } from "@mui/material";
import { GainLossTypography } from "@rentablo/components";
import { toPercent } from "@helpers";

const StyledChip = styled(Chip)(({ theme }) => ({
  // Holds the visually hidden label: positioned against the page instead, the
  // labels of chips scrolled off to the right widened the whole page.
  position: "relative",
  flex: "none",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.border.secondary}`,
  borderRadius: theme.spacing(4),
  // Tighter than the theme's xsmall padding, which a single class can't beat.
  "&.MuiChip-root.MuiChip-sizeXsmall": {
    padding: theme.spacing(0.5, 2),
  },
  ".MuiChip-label": {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

// Keeps the label for screen readers when the visible labels are switched off.
const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

interface Props {
  label: string;
  showLabel: boolean;
  // Shown instead of the label while the labels are off.
  icon: ReactNode;
  value: ReactNode;
  change?: number;
}

export const MetricChip = ({
  label,
  showLabel,
  icon,
  value,
  change,
}: Props) => (
  <StyledChip
    size="xsmall"
    data-testid="investment-metric-chip"
    label={
      <>
        {showLabel ? (
          <Typography variant="caption" color="textSecondary" component="span">
            {label}
          </Typography>
        ) : (
          <>
            <Box
              component="span"
              display="inline-flex"
              color="text.secondary"
              aria-hidden
            >
              {icon}
            </Box>
            <Box component="span" sx={visuallyHidden}>
              {label}
            </Box>
          </>
        )}
        {change !== undefined && (
          <GainLossTypography
            variant="caption2"
            component="span"
            value={change}
          >
            {toPercent(change, "exceptZero")}
          </GainLossTypography>
        )}
        <Typography variant="caption2" color="textPrimary" component="span">
          {value}
        </Typography>
      </>
    }
  />
);
